import { FwVersion } from "../hooks/useDevices";
import { EncoderCalibStatus, IReceivedPacketTypes, FaultCode, SmFwBuildType, HallsensorCalibStatus, HallsensorDirection, ControllerState } from "./common-types";

// Allows for compile-time validation of the exhaustiveness of switches on enums.
// If this is used for a numeric enum, use with StrictNumericEnumParam.
export const assertUnreachable = (_x: never): never => {
    throw new Error("Unexpected code path!");
};

// Make it a compile time error to pass `number` into a function that takes a numeric Enum.
// Example:
//   export const fnThatTakesAnEnum = <Value extends YourEnumType>( yourEnumParam: StrictNumericEnumParam<YourEnumType, Value> ) => {
//     ...
//   }
// Kudos to UselessPickles for this: https://github.com/Microsoft/TypeScript/issues/26362#issuecomment-476018475
export type StrictNumericEnumParam<Enum extends number, Param extends Enum> =
    true extends (
        { [key: number]: false } & { [P in Enum]: true }
    )[Enum] ? (
        true extends (
            { [key: number]: false } & { [P in Enum]: true }
        )[Param] ? Param : never
    ) : Enum;

export enum Env {
  Prod,
  Dev,
  Legacy,
  Sandbox,
  Local,
};

// Get current environment from environment variable.
// Throws exception if invalid.
export const getEnv = (): Env => {
  switch ( import.meta.env.VITE_HOST_ENV ) {
      case "prod": return Env.Prod;
      case "dev": return Env.Dev;
      case "legacy": return Env.Legacy;
      case "sandbox": return Env.Sandbox;
      case "local": return Env.Local;
      default: throw new Error( `Invalid value for VITE_HOST_ENV: ${ import.meta.env.VITE_HOST_ENV }` );
  }
};

export let appHost: string;
const env = getEnv();
switch ( env ) {
    case Env.Prod: appHost = "https://app.salientmotion.com"; break;
    case Env.Dev: appHost = "https://app-dev.salientmotion.com"; break;
    case Env.Legacy: appHost = "https://legacy-app-dev.salientmotion.com"; break;
    case Env.Sandbox: appHost = "https://sandbox.salientmotion.com"; break;
    case Env.Local: appHost = "http://localhost:3000"; break;
    default: assertUnreachable( env );
}

export const isProd = () => getEnv() === Env.Prod;

type PacketKeysType = { [ I in keyof IReceivedPacketTypes ]: keyof IReceivedPacketTypes[ I ] };
export type PacketKeys<I extends keyof IReceivedPacketTypes> = PacketKeysType[ I ];

const capitalize = ( str: string ): string => {
    return str.charAt( 0 ).toUpperCase() + str.substring( 1 );
};

export const camelCaseToReadable = ( str: string ): string => {
    const words = str.match( /[A-Za-z][a-z]*/g ) || [];

    return words.map( capitalize ).join( " " );
};

export const downloadText = ( filename: string, text: string ) => {
    const a = document.createElement( "a" );
    a.style.display = "none";
    a.setAttribute( "href", "data:text/plain;charset=utf-8," + encodeURIComponent( text ) );
    a.setAttribute( "download", filename );

    document.body.appendChild( a );
    a.click();

    document.body.removeChild( a );
};

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
export const throttle = <T extends Function>( callback: T, ms: number, debounce: boolean = true ): T => {
    let lastTime: number = 0;
    let debounceTimeout: NodeJS.Timeout | undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return ( ( ...args: any[] ) => {
        const now = nowMs();
        if ( now - lastTime >= ms ) {
            clearTimeout( debounceTimeout );
            callback( ...args );
            lastTime = nowMs();
        } else if ( debounce ) {
            // Clear and reset debounce timeout
            clearTimeout( debounceTimeout );
            debounceTimeout = setTimeout( () => {
                callback( ...args );
                lastTime = nowMs();
            }, ms );
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } ) as any;
};

export const nowMs = () => ( new Date() ).getTime();

export type ValueRange = [ number, number ];

export const moveValueRange = (
    [ windowLow, windowHigh ]: ValueRange,
    [ valueMin, valueMax ]: ValueRange,
    delta: number,
): ValueRange => {
    const currentWindowSize = windowHigh - windowLow;
    const newLow = Math.min( Math.max( windowLow + delta, valueMin ), valueMax - currentWindowSize );

    return [ newLow, newLow + currentWindowSize ];
}

export const controllerStateToString = <Value extends ControllerState>( controllerState: StrictNumericEnumParam<ControllerState, Value> ): string => {
    switch ( controllerState ) {
        case ControllerState.BOOT: return "Boot";
        case ControllerState.CALIBRATION: return "Calibration";
        case ControllerState.L_R_DETECTION: return "Parameter Detection";
        case ControllerState.ENCODER_DETECTION: return "Encoder Detection";
        case ControllerState.STOPPED: return "Stopped";
        case ControllerState.RUNNING: return "Running";
        case ControllerState.TEST: return "Test";
        case ControllerState.UPDATING_FIRMWARE: return "Updating Firmware";
        case ControllerState.FAULT: return "Fault";
        case ControllerState.HALL_SENSOR_DETECTION: return "Hall Sensor Detection";
        default: return assertUnreachable(controllerState);
    }
};

export const faultCodeToString = <Value extends FaultCode>( faultCode: StrictNumericEnumParam<FaultCode, Value> ): string => {
    switch ( faultCode ) {
        case FaultCode.NO_FAULT: return "None";
        case FaultCode.BAD_CALIBRATION_CURRENT_MEASURE: return "Hardware Fault";
        case FaultCode.VOLTAGE_OUT_OF_RANGE_CRITICAL: return "Voltage Out Of Range";
        case FaultCode.OVERTEMPERATURE: return "Overtemperature";
        case FaultCode.OVERCURRENT_CRITICAL: return "Overcurrent";
        case FaultCode.BAD_CONFIG: return "Bad Config";
        case FaultCode.GATE_DRIVE_UNDERVOLTAGE: return "Gate Drive Undervoltage";
        case FaultCode.LOGIC_UNDERVOLTAGE_Deprecated: return "Logic Undervoltage";
        case FaultCode.PARAM_DETECT_FAIL: return "Parameter Detection Failure";
        case FaultCode.NAN_OUTPUT_PHASE_VOLTAGE: return "NaN Output";
        case FaultCode.BAD_PARAM_Deprecated: return "Bad Parameter";
        case FaultCode.INPUT_TIMEOUT: return "Invalid Input";
        case FaultCode.ENCODER_CALIBRATION_FAILED: return "Encoder Calibration Failed";
        case FaultCode.GATE_DRIVE_OVERCURRENT: return "Gate Drive Overcurrent";
        case FaultCode.GATE_DRIVE_FAULT: return "Gate Drive Fault";
        case FaultCode.GATE_DRIVE_OVERTEMPERATURE_SHUTDOWN: return "Gate Drive Overtemp. Shutdown";
        case FaultCode.GATE_DRIVE_OVERTEMPERATURE_WARNING: return "Gate Drive Overtemp. Warning";
        case FaultCode.GATE_DRIVE_CHARGE_PUMP_VGLS_UNDERVOLTAGE: return "Gate Drive Charge Pump Vgls Undervoltage";
        case FaultCode.GATE_DRIVE_INIT_FAILED: return "Gate Drive Init Failed";
        case FaultCode.HALL_SENSOR_CALIBRATION_FAILED: return "Hall Sensor Calibration Failed";
        case FaultCode.VOLTAGE_OUT_OF_RANGE_WARNING: return "Voltage Out Of Range Warning";
        case FaultCode.OVERCURRENT_WARNING: return "Overcurrent Warning";
        case FaultCode.OVERLOAD_CRITICAL: return "Critical Overload";
        case FaultCode.DC_OVERCURRENT_CRITICAL: return "Critical DC Overcurrent";
        case FaultCode.NULL_KEYSTORE_CALLBACK: return "NULL KeyStore Callback";
        case FaultCode.CRC_32_MISMATCH: return "CRC32 Mismatch";
        default: return assertUnreachable(faultCode);
    }
};

export const encoderCalibrationStatusToString = <Value extends EncoderCalibStatus>( status: string | StrictNumericEnumParam<EncoderCalibStatus, Value> ): string => {
    if ( typeof status === "string" ) return status;
    switch ( status ) {
        case EncoderCalibStatus.UNKNOWN: return "Not Calibrated";
        case EncoderCalibStatus.COMPLETE: return "Calibrated";
        case EncoderCalibStatus.ONGOING: return "Ongoing";
        case EncoderCalibStatus.FAILURE_INDEX_MISSING: return "Failure: Index Missing";
        case EncoderCalibStatus.FAILURE_POSITION_ERROR: return "Failure: Position Error";
        case EncoderCalibStatus.FAILURE_TIMEOUT: return "Failure: Timeout";
        case EncoderCalibStatus.FAILURE_ABORTED: return "Failure: Calibration Aborted";
        default: return assertUnreachable(status);
    }
}

export const encoderCalibrationFailed = ( status: string | EncoderCalibStatus | undefined ): boolean => {
      if ( typeof status === "string" ) return true;
      return status === EncoderCalibStatus.FAILURE_INDEX_MISSING ||
             status === EncoderCalibStatus.FAILURE_POSITION_ERROR ||
             status === EncoderCalibStatus.FAILURE_TIMEOUT ||
             status === EncoderCalibStatus.FAILURE_ABORTED;
}

export const hallSensorCalibrationStatusToString = <Value extends HallsensorCalibStatus>( status: string | StrictNumericEnumParam<HallsensorCalibStatus, Value> ): string => {
    if ( typeof status === "string" ) return status;
    switch ( status ) {
        case HallsensorCalibStatus.UNKNOWN: return "Not Calibrated";
        case HallsensorCalibStatus.COMPLETE: return "Calibrated";
        case HallsensorCalibStatus.ONGOING: return "Ongoing";
        case HallsensorCalibStatus.FAILURE_TIMEOUT: return "Failure: Timeout";
        case HallsensorCalibStatus.FAILURE_ABORTED: return "Failure: Calibration Aborted";
        default: return assertUnreachable(status);
    }
}

export const hallSensorCalibrationFailed = ( status: string | HallsensorCalibStatus | undefined ): boolean => {
    if ( typeof status === "string" ) return true;
    return status === HallsensorCalibStatus.FAILURE_TIMEOUT ||
           status === HallsensorCalibStatus.FAILURE_ABORTED;
}

export const hallSensorDirectionToString = <Value extends HallsensorDirection>( direction: StrictNumericEnumParam<HallsensorDirection, Value> ): string | undefined => {
    switch ( direction ) {
        case HallsensorDirection.UNKNOWN: return undefined
        case HallsensorDirection.REVERSE: return "Reverse";
        case HallsensorDirection.FORWARD: return "Forward";
        default: return assertUnreachable(direction);
    }
}

export const versionLessThan = ( v1: FwVersion, v2: FwVersion ): boolean => {
    for ( let i = 0; i < v1.length; i++ ) {
        if ( v1[ i ] < v2[ i ] ) {
            return true;
        } else if ( v1[ i ] > v2[ i ] ) {
            return false;
        }
    }

    return false;
}

export type Strings<T> = { [K in keyof T]: string };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const strings = (obj: any) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stringsObj: any = {};
  for (const key of Object.keys(obj)) {
    stringsObj[key] = obj[key].toString();
  }
  return stringsObj;
};

export const rpmToRadps = ( rpm: number ): number => {
    return rpm * 2 * Math.PI / 60;
}

export const radpsToRpm = ( radPerSec: number): number => {
    return radPerSec / 2 / Math.PI * 60;
}

export const secondsToMilliseconds = ( seconds: number ): number => {
    return seconds * 1000;
}

// Must validate that polePairCount is a positive integer.
export const electricalToMechanical = ( elVal: number, polePairCount: number ): number => {
  return elVal / polePairCount;
};

export const mechanicalToElectrical = ( mechVal: number, polePairCount: number ): number => {
  return mechVal * polePairCount;
};

export const isPolePairCountValid = ( polePairCount: number ): boolean => {
  return ( Number.isInteger( polePairCount ) && polePairCount > 0 );
};

export const buildTypeIntToString = (buildTypeInt: number | undefined ): string => {
    if ( buildTypeInt === undefined || Number.isInteger(buildTypeInt) === false )
    {
        return "Unknown Build Type";
    }

    return SmFwBuildType[buildTypeInt];
};

export const downloadData = ( fileName: string, data: string | Uint8Array ) => {
    let uriEncodedData: string;
    let type;
    if ( typeof data === "string" ) {
        type = 'text/plain';
        uriEncodedData = encodeURIComponent( data );
    } else {
        type = 'application/octet-stream';
        uriEncodedData = Array.from(data, (byte) => {
            return '%' + ('0' + (byte & 0xff).toString(16)).slice(-2);
        }).join('');
    }
    const a = document.createElement( "a" );
    a.style.display = "none";
    a.setAttribute( "href", `data:${ type };charset=utf-8,` + uriEncodedData );
    a.setAttribute( "download", fileName );

    document.body.appendChild( a );
    a.click();

    document.body.removeChild( a );
};

// Warning: not constant time! Only matters for handshake
export const areByteArraysEqual = ( a1: Uint8Array, a2: Uint8Array ): boolean => {
    if ( a1.length !== a2.length ) {
        return false;
    }
    for ( let i = 0; i < a1.length; i++ ) {
        if ( a1[i] !== a2[i] ) {
            return false;
        }
    }
    return true;
};

// Warning: not constant time! Only matters for handshake, but even there, the
// current usage place is OK.
export const bytesAllZero = ( a: number[] | Uint8Array ): boolean => {
    for ( let i = 0; i < a.length; i++ ) {
        if ( a[i] !== 0 ) return false;
    }
    return true;
}

// Note: there is no file extension in the returned string.
export const filenameWithTimestamp = ( prefix: string ): string => {
    const date = new Date();
    return `${ prefix }-${ date.toISOString().replace(/[-:.]/g, "").slice(0, -4) }`;
};

// Is the user prevented from accessing app-dev (our development environment)?
export const isUserBlocked = ( userEmail: string | null | undefined ): boolean => {
    const suffixes = [ /@salientmotion.com$/i ];
    const externalEmail = !suffixes.some((s) => userEmail?.match(s));

    return !isProd() && externalEmail;
};

export const P_GAIN_TEXT = "Proportional gain refers to how quickly a motor reacts to an error (difference between desired and actual position) based on the current error size.";
export const I_GAIN_TEXT = "Integral gain determines how much the motor continues to adjust based on the accumulated error over time, effectively eliminating any steady-state error by progressively correcting for past deviations.";
export const D_GAIN_TEXT = "Derivative gain responds to the rate of change of the error, essentially acting as a damping mechanism to prevent rapid changes and reduce overshoot, but can also introduce noise sensitivity if set too high.";
export const MOTOR_MAX_SPEED_TEXT = "The maximum speed that the motor will move at, in radians per second.";
export const MOTOR_MAX_ACCELERATION_TEXT = "The maximum acceleration that the motor will move at, in radians per second squared.";
export const DEADBAND_TEXT = "The size of the acceptable range for the target position, in the configured position units.";
export const POSITION_TYPE_TEXT = "Use linear or rotational position units.";
export const POSITION_UNITS_TEXT = "The position units that the position is measured in.";
export const RESOLUTION_TEXT = "The number of decimal places of the position command.";
export const TRAVEL_DISTANCE_TEXT = "The maximum distance motor travels on the shaft.";
